import React, { useEffect, useState, useRef, useCallback } from 'react';
import './TextPrompt.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ButtonSidebar2 from './ButtonSidebar/ButtonSidebar2';
import { useHistory, Prompt } from 'react-router-dom';
import arrowSVG from './Iconos/arrow.svg';
import checkSVG from './Iconos/check.svg';
import edit2SVG from './Iconos/edit-2.svg';
import TextToneAndHashtagSelector from './TextToneAndHashtagSelector';

const VALID_MINUTES = 15;

function TextPrompt() {
  const history = useHistory();
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [generatedText, setGeneratedText] = useState('');
  const [selectedTone, setSelectedTone] = useState('neutral');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [isProcessStarted, setIsProcessStarted] = useState(false);

  const editableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Verificar si el usuario está autenticado
    const userId = localStorage.getItem('userId');
    if (!userId) {
      // Redirigir a la página de inicio de sesión si no está autenticado
      history.push('/PantallaInicial');
    }
  }, [history]);

  const handleToneChange = async (newTone: string) => {
    setSelectedTone(newTone);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/change-tone`, {
        text: generatedText,
        tone: newTone,
      });
      setGeneratedText(response.data);
    } catch (error) {
      console.error("Error changing text tone:", error);
    }
  };

  const handleSaveDraft = async () => {
    if (!generatedText.trim()) {
      alert('El campo de texto no puede estar vacío.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('No estás autenticado.');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/save-text-draft`, {
        contenido_texto: generatedText
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      alert('Borrador de texto guardado con éxito');
    } catch (error) {
      console.error('Error al guardar el borrador de texto:', error);
      alert('Error al guardar el borrador de texto');
    }
  };

  const handleAddHashtag = (newHashtag: any) => {
    setHashtags([...hashtags, newHashtag]);
  };

  const saveTextToLocalStorage = useCallback(() => {
    const textData = {
      timestamp: new Date().getTime(),
      content: generatedText,
    };
    localStorage.setItem('textData', JSON.stringify(textData));
  }, [generatedText]);

  const placeholder = t('Placeholder');

  const handleTextChange = (e: React.FormEvent<HTMLDivElement>) => {
    const sel = window.getSelection();
    const range = sel?.getRangeAt(0);
    const startOffset = range?.startOffset;

    setGeneratedText(e.currentTarget.textContent || '');

    setTimeout(() => {
      if (range && editableRef.current) {
        const newRange = document.createRange();
        const selection = window.getSelection();
        newRange.setStart(editableRef.current.childNodes[0], startOffset ?? 0);
        newRange.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(newRange);
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsProcessStarted(true); // Marcar que el proceso ha comenzado

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/generate-description`, { prompt });
      const respuesta = response.data.content;
      setGeneratedText(respuesta);
    } catch (error) {
      console.error("Error generating description:", error);
    }
  };

  // Controlar navegación sin advertencia para rutas específicas
  const handleNavigation = (nextPath: string) => {
    history.push(nextPath);
  };

  const handlePreviousClick = () => {
    handleNavigation('/Social-Networking');
  };

  const handleNextClick = () => {
    handleNavigation('/Social-Networking3');
  };

  const handleOptimizeText = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await axios.post(`${backendUrl}/optimize-text`, {
        text: generatedText,
      });
      setGeneratedText(response.data.text);
    } catch (error) {
      console.error("Error optimizing text:", error);
    }
  };

  useEffect(() => {
    const textDataJSON = localStorage.getItem('textData');
    if (textDataJSON) {
      const textData = JSON.parse(textDataJSON);
      const timeNow = new Date().getTime();
      const timeElapsed = (timeNow - textData.timestamp) / 1000 / 60;

      if (timeElapsed < VALID_MINUTES) {
        setGeneratedText(textData.content);
      } else {
        localStorage.removeItem('textData');
      }
    }
  }, []);

  useEffect(() => {
    if (generatedText) {
      saveTextToLocalStorage();
    }
  }, [generatedText, saveTextToLocalStorage]);

  const addHashtagToText = (hashtag: string) => {
    setGeneratedText((prevText) => `${prevText} ${hashtag}`);
  };

  return (
    <div className='container-text'>
      <Prompt
        when={isProcessStarted}
        message={(location) =>
          location.pathname === '/Social-Networking3' || location.pathname === '/Social-Networking'
            ? true
            : '¿Estás seguro de que quieres salir? Se han descontado  3 créditos por este proceso.'
        }
      />
      <div className='text-container'>
        <TextToneAndHashtagSelector
          onToneChange={handleToneChange}
          onHashtagAdd={handleAddHashtag}
          selectedTone={selectedTone}
          hashtags={hashtags}
          addHashtagToText={addHashtagToText}
        />
        <div className="text-prompt-container">
          <div className="image-prompt-title">
            <p>{t('TextPrompt.describeYourImage')} <span className="highlight">{t('TextPrompt.ImageHere')}</span>.</p>
          </div>
          <div className="text-prompt-description">
            <form onSubmit={handleSubmit}>
              <input
                className='text-input-prompt'
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={placeholder}
              />
              <button className="select-button2" type="submit">
                <span className="image-prompt-button-icon"></span>
                <div className='text-prompt-image'>
                  <div><img src={edit2SVG} alt="" /></div>
                  <div>{t('TextPrompt.create')}</div>
                </div>
              </button>
            </form>
          </div>
          <div className="generated-text-container">
            <div
              ref={editableRef}
              className='generated-text'
              contentEditable
              onInput={handleTextChange}
              suppressContentEditableWarning={true}
            >
              {generatedText}
            </div>
          </div>
         
          <div className="image-selection-navigation">
            <button className="image-selection-previous" onClick={handlePreviousClick}>
              <img src={arrowSVG} alt="" className='reverse-arrow' /> {t('Anterior')}
            </button>
            <button className="image-selection-next" onClick={handleNextClick}>
              {t('Siguiente')} <img src={arrowSVG} alt="" className='normal-arrow' />
            </button>
          </div>
        </div>
      </div>
      <ButtonSidebar2
        onSaveDraft={handleSaveDraft}
        onEdit={function (): void {
          throw new Error('Function not implemented.');
        }}
        onOptimize={handleOptimizeText}
      />
    </div>
  );
}

export default TextPrompt;
